'use client'

import { useState } from 'react'
import { Heart, Menu, MessageCircle, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { VisitorCounter } from '@/components/visitor-counter'
import { AuthBadge } from '@/components/auth-badge'
import { NotificationBell } from '@/components/notification-bell'

const navLinks = [
  { label: 'Why NewEmail', href: '/#features' },
  { label: 'How it works', href: '/#get-started' },
  { label: 'Downloads', href: '/downloads' },
  { label: 'Guide', href: '/docs' },
  { label: 'Forum', href: '/forum' },
]

export function SiteHeader() {
  const [open, setOpen] = useState(false)

  return (
    <header className="sticky top-0 z-50 border-b border-border/60 bg-background/90 backdrop-blur-xl">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <a href="/" className="flex items-center gap-2">
            <img
              src="/newemail-logo.svg"
              alt="NewEmail logo"
              className="h-9 w-9 rounded-xl shadow-sm"
              width={36}
              height={36}
            />
            <span className="text-lg font-extrabold tracking-tight">
              New<span className="text-primary">Email</span>
            </span>
          </a>
          <VisitorCounter />
        </div>

        <nav className="hidden items-center gap-6 lg:flex">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              {link.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <NotificationBell />
          <Button
            variant="outline"
            size="sm"
            nativeButton={false}
            render={<a href="/register" />}
          >
            Join the forum
          </Button>
          <Button
            variant="outline"
            size="sm"
            nativeButton={false}
            render={<a href="/donate" aria-label="Donate to the NewEmail project" />}
          >
            <Heart className="h-3.5 w-3.5 text-red-500" />
            Donate
          </Button>
          <Button size="sm" nativeButton={false} render={<a href="/downloads" />}>
            Download
          </Button>
          <span aria-hidden className="mx-1 h-6 w-px bg-border" />
          <AuthBadge />
        </div>

        <div className="flex items-center gap-1 lg:hidden">
          <NotificationBell />
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="flex h-10 w-10 items-center justify-center rounded-lg text-foreground"
            aria-label="Toggle menu"
            aria-expanded={open}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border/60 bg-background lg:hidden">
          <nav className="flex flex-col gap-1 px-4 py-4">
            {[...navLinks, { label: 'Community', href: '/community' }].map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                className="rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-secondary hover:text-foreground"
              >
                {link.label}
              </a>
            ))}
            <Button
              className="mt-2"
              variant="outline"
              nativeButton={false}
              render={<a href="/login" onClick={() => setOpen(false)} />}
            >
              Log in
            </Button>
            <Button
              nativeButton={false}
              render={<a href="/downloads" onClick={() => setOpen(false)} />}
            >
              Download NewEmail
            </Button>
            <Button
              variant="outline"
              nativeButton={false}
              render={
                <a
                  href="/donate"
                  aria-label="Donate to the NewEmail project"
                  onClick={() => setOpen(false)}
                />
              }
            >
              <Heart className="h-4 w-4 text-red-500" />
              Donate
            </Button>
            <Button
              variant="outline"
              nativeButton={false}
              render={<a href="/forum" onClick={() => setOpen(false)} />}
            >
              <MessageCircle className="h-4 w-4" />
              Visit the forum
            </Button>
            <AuthBadge mobile />
          </nav>
        </div>
      )}
    </header>
  )
}
